import React, { useState } from 'react';
import { SCRIPTURES, SCRIPTURE_INDEX, SCRIPTURE_CATEGORIES } from '../../data/scriptures';
import { PostersSection, PosterViewerModal } from '../posters/PosterViewer';

const granthState = { open: false, focus: null, listeners: new Set() };

export function openGrantha(id = null) {
  granthState.open = true; granthState.focus = id;
  granthState.listeners.forEach(fn => fn());
}

export function closeGrantha() {
  granthState.open = false; granthState.focus = null;
  granthState.listeners.forEach(fn => fn());
}

export function useGranthState() {
  const [, setT] = useState(0);
  React.useEffect(() => {
    const fn = () => setT(t => t + 1);
    granthState.listeners.add(fn);
    return () => granthState.listeners.delete(fn);
  }, []);
  return { open: granthState.open, focus: granthState.focus };
}

let __reciteCtx = null;
export function reciteTone(freq = 220) {
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return;
    if (!__reciteCtx) __reciteCtx = new Ctor();
    const ctx = __reciteCtx;
    const now = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = freq;
    g.gain.setValueAtTime(0, now);
    g.gain.linearRampToValueAtTime(0.12, now + 0.04);
    g.gain.exponentialRampToValueAtTime(0.001, now + 2.8);
    const o2 = ctx.createOscillator();
    const g2 = ctx.createGain();
    o2.type = "sine";
    o2.frequency.value = freq * 1.5;
    g2.gain.setValueAtTime(0, now);
    g2.gain.linearRampToValueAtTime(0.04, now + 0.05);
    g2.gain.exponentialRampToValueAtTime(0.001, now + 2.0);
    o.connect(g).connect(ctx.destination);
    o2.connect(g2).connect(ctx.destination);
    o.start(now); o2.start(now);
    o.stop(now + 3); o2.stop(now + 2.2);
  } catch (e) {}
}

function PalmLeaf({ scripture, onClose }) {
  const [leaf, setLeaf] = useState(0);
  const s = scripture;
  const totalPages = s.leaves.length + 1;
  const next = () => { if (leaf < s.leaves.length) { setLeaf(l => l + 1); reciteTone(220 + leaf * 18); } };
  const prev = () => { if (leaf > 0) { setLeaf(l => l - 1); reciteTone(220 + leaf * 18); } };

  React.useEffect(() => { setLeaf(0); reciteTone(196); }, [scripture.id]);

  const yugaColor = {
    satya: "#d4a44a",
    treta: "#d97a3c",
    dvapara: "#a8c8e8",
    kali: "#6a6058"
  }[s.yuga] || "#d4a44a";

  return (
    <div className="palm-leaf-stage">
      <div className="palm-leaf">
        <div className="leaf-edge top" />
        <div className="leaf-edge bottom" />
        <div className="leaf-hole left" />
        <div className="leaf-hole right" />

        <header className="leaf-head">
          <div className="leaf-deva" style={{ color: yugaColor, textShadow: `0 0 22px ${yugaColor}` }}>{s.deva}</div>
          <div className="leaf-trans" style={{ color: yugaColor }}>{s.trans}</div>
          <div className="leaf-era">{s.title} · {s.era}</div>
        </header>

        {leaf === 0 ? (
          <div className="leaf-body">
            <div className="leaf-subtitle">{s.subtitle}</div>
            <p className="leaf-prose">{s.intro}</p>
            {s.author && (
              <p style={{ fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.2em", color: "#a89070", textAlign: "center", marginTop: "16px", textTransform: "uppercase" }}>
                {s.author}
              </p>
            )}
            <div className="leaf-cta">Turn the leaf to read the verses →</div>
          </div>
        ) : (
          <div className="leaf-body verse">
            <div className="leaf-num">verse {leaf} of {s.leaves.length}</div>
            <div className="leaf-deva-verse" style={{ color: yugaColor }}>{s.leaves[leaf - 1].deva}</div>
            <div className="leaf-trans-verse">— {s.leaves[leaf - 1].trans} —</div>
            <p className="leaf-meaning">"{s.leaves[leaf - 1].meaning}"</p>
          </div>
        )}

        <footer className="leaf-foot">
          <button className="leaf-btn" disabled={leaf === 0} onClick={prev}>← previous leaf</button>
          <div className="leaf-dots">
            {Array.from({ length: totalPages }).map((_, i) => (
              <span key={i} className={`dot ${i === leaf ? "on" : ""}`} onClick={() => { setLeaf(i); reciteTone(220 + i * 18); }} />
            ))}
          </div>
          <button className="leaf-btn" disabled={leaf >= s.leaves.length} onClick={next}>next leaf →</button>
        </footer>
      </div>
      <button className="leaf-close" onClick={onClose}>return to the library ×</button>
    </div>
  );
}

function CategoryBar({ active, onSelect }) {
  return (
    <div style={{
      display: "flex",
      flexWrap: "wrap",
      gap: "8px",
      justifyContent: "center",
      marginBottom: "32px",
      padding: "0 20px",
    }}>
      <button
        onClick={() => onSelect(null)}
        style={{
          padding: "8px 16px",
          background: active === null ? "rgba(212,164,74,0.2)" : "transparent",
          border: `1px solid ${active === null ? "#d4a44a" : "rgba(212,164,74,0.25)"}`,
          color: active === null ? "#d4a44a" : "#a89070",
          fontFamily: "var(--mono)",
          fontSize: "10px",
          letterSpacing: "0.2em",
          textTransform: "uppercase",
          cursor: "pointer",
          transition: "all 250ms",
        }}
      >
        All Texts
      </button>
      {SCRIPTURE_CATEGORIES.map(cat => (
        <button
          key={cat.id}
          onClick={() => onSelect(cat.id)}
          style={{
            padding: "8px 14px",
            background: active === cat.id ? "rgba(212,164,74,0.2)" : "transparent",
            border: `1px solid ${active === cat.id ? "#d4a44a" : "rgba(212,164,74,0.25)"}`,
            color: active === cat.id ? "#d4a44a" : "#a89070",
            fontFamily: "var(--mono)",
            fontSize: "10px",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            cursor: "pointer",
            transition: "all 250ms",
          }}
        >
          {cat.deva} {cat.label}
        </button>
      ))}
    </div>
  );
}

export function Granthalaya() {
  const { open, focus } = useGranthState();
  const [selected, setSelected] = useState(null);
  const [category, setCategory] = useState(null);
  const [viewingPoster, setViewingPoster] = useState(null);

  React.useEffect(() => { setSelected(focus); }, [focus, open]);
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setSelected(null); closeGrantha(); } };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  const filtered = category
    ? SCRIPTURES.filter(s => s.category === category)
    : SCRIPTURES;

  const groupedByYuga = filtered.reduce((acc, s) => {
    if (!acc[s.yuga]) acc[s.yuga] = [];
    acc[s.yuga].push(s);
    return acc;
  }, {});

  const yugaOrder = ["satya", "treta", "dvapara", "kali"];
  const yugaLabels = {
    satya: { name: "Satya Yuga · सत्य युग", sub: "The Age of Truth" },
    treta: { name: "Treta Yuga · त्रेता युग", sub: "The Age of Three-Quarters" },
    dvapara: { name: "Dvapara Yuga · द्वापर युग", sub: "The Age of Halves" },
    kali: { name: "Kali Yuga · कलि युग", sub: "The Age of Darkness" },
  };

  return (
    <div className="granthalaya">
      <div className="granthalaya-sky" />
      <header className="granthalaya-head">
        <div className="g-deva">ग्रन्थालय</div>
        <div className="g-title">Granthalaya · the hall of remembered books</div>
        <button className="g-close" onClick={() => { setSelected(null); closeGrantha(); }}>return to the story ×</button>
      </header>

      <CategoryBar active={category} onSelect={setCategory} />

      {!selected ? (
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 20px" }}>
          <p style={{
            fontFamily: "var(--mono)",
            fontSize: "11px",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: "#a89070",
            textAlign: "center",
            marginBottom: "40px",
          }}>
            {filtered.length} texts · browse by yuga or choose a category above
          </p>

          {yugaOrder.map(yuga => {
            const texts = groupedByYuga[yuga];
            if (!texts || texts.length === 0) return null;
            const yugaInfo = yugaLabels[yuga];

            const catColor = {
              satya: "#d4a44a",
              treta: "#d97a3c",
              dvapara: "#a8c8e8",
              kali: "#6a6058"
            }[yuga] || "#d4a44a";

            return (
              <section key={yuga} style={{ marginBottom: "48px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "20px" }}>
                  <div style={{ width: "60px", height: "1px", background: `linear-gradient(90deg, transparent, ${catColor})` }} />
                  <div>
                    <div style={{ fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.3em", color: catColor, textTransform: "uppercase" }}>
                      {yugaInfo.name}
                    </div>
                    <div style={{ fontFamily: "var(--display)", fontStyle: "italic", fontSize: "14px", color: "#a89070" }}>
                      {yugaInfo.sub}
                    </div>
                  </div>
                  <div style={{ flex: 1, height: "1px", background: `linear-gradient(90deg, ${catColor}, transparent)` }} />
                </div>

                <div className="granthalaya-grid">
                  {texts.map(s => (
                    <button
                      key={s.id}
                      className={`grantha-card y-${s.yuga}`}
                      onClick={() => { setSelected(s.id); reciteTone(180); }}
                      style={{ textAlign: "left" }}
                    >
                      <div className="card-deva" style={{ textShadow: `0 0 18px ${catColor}` }}>{s.deva}</div>
                      <div className="card-title">{s.title}</div>
                      <div className="card-sub">{s.subtitle}</div>
                      <div style={{
                        fontFamily: "var(--mono)",
                        fontSize: "9px",
                        letterSpacing: "0.15em",
                        color: catColor,
                        textTransform: "uppercase",
                        marginTop: "8px",
                      }}>
                        {s.category} · {s.leaves.length} verses
                      </div>
                      <div className="card-era">{s.era}</div>
                    </button>
                  ))}
                </div>
              </section>
            );
          })}

          <div style={{ marginTop: "64px", marginBottom: "32px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "24px" }}>
              <div style={{ width: "60px", height: "1px", background: "linear-gradient(90deg, transparent, #d4a44a)" }} />
              <div>
                <div style={{ fontFamily: "var(--mono)", fontSize: "11px", letterSpacing: "0.3em", color: "#d4a44a", textTransform: "uppercase" }}>
                  Posters · ध्वनिपट
                </div>
                <div style={{ fontFamily: "var(--display)", fontStyle: "italic", fontSize: "14px", color: "#a89070" }}>
                  Sacred imagery for your space
                </div>
              </div>
              <div style={{ flex: 1, height: "1px", background: "linear-gradient(90deg, #d4a44a, transparent)" }} />
            </div>
            <PostersSection onViewPoster={setViewingPoster} />
          </div>
        </div>
      ) : (
        <PalmLeaf scripture={SCRIPTURE_INDEX[selected]} onClose={() => setSelected(null)} />
      )}

      {viewingPoster && (
        <PosterViewerModal
          poster={viewingPoster}
          onClose={() => setViewingPoster(null)}
        />
      )}
    </div>
  );
}

export function GranthalayaToggle() {
  return (
    <button className="om-toggle" onClick={() => openGrantha()} aria-label="Open the Granthalaya">
      <span className="om-glyph" style={{ fontSize: 18 }}>ग्र</span>
      <span className="om-label">granthalaya</span>
    </button>
  );
}

export function ScriptureSeal({ id, label, hint }) {
  const s = SCRIPTURE_INDEX[id];
  if (!s) return null;
  return (
    <button className="scripture-seal" onClick={() => openGrantha(id)}>
      <span className="seal-deva">{s.deva}</span>
      <span className="seal-body">
        <span className="seal-label">{label || s.title}</span>
        <span className="seal-hint">{hint || "open this granth →"}</span>
      </span>
    </button>
  );
}
